/**
 * Creator-program shared types (M1).
 * Mirrors the DB shapes defined in:
 *   supabase/migrations/20260503130000_users_creator_role.sql
 *   supabase/migrations/20260503140000_agent_template_versions.sql
 */

import type { AgentTemplate } from './agent';

export type AgentTemplateKind = 'official' | 'creator' | 'system';
export type AgentTemplateVersionStatus = 'draft' | 'pending' | 'live' | 'superseded' | 'rejected';
export type AgentTemplateLifecycleStatus = 'draft' | 'live' | 'paused';
export type AgentApprovalDecision = 'approved' | 'changes_requested' | 'rejected';

/** Subset of `agent_templates` columns the studio + admin queue care about. */
export interface CreatorAgentTemplateRow {
  id: string;
  name: string;
  slug: string | null;
  hy_user_id: string | null;
  template_kind: AgentTemplateKind;
  version_status: AgentTemplateLifecycleStatus;
  current_version_id: string | null;
  paused_reason: string | null;
  rewards_enabled: boolean;
  is_active: boolean;
  cover_media_url: string | null;
  cover_media_type: 'image' | 'video' | null;
  public_description: string | null;
  updated_at: string;
  created_at: string;
}

export interface AgentTemplateVersionRow {
  id: string;
  template_id: string;
  version_number: number;
  version_status: AgentTemplateVersionStatus;
  payload: Record<string, unknown>;
  preflight_report: Record<string, unknown> | null;
  decision_reason: string | null;
  decided_by_hy_user_id: string | null;
  decided_at: string | null;
  submitted_at: string | null;
  created_by_hy_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface AgentTemplateApprovalRow {
  id: string;
  version_id: string;
  template_id: string;
  decision: AgentApprovalDecision;
  reason: string | null;
  decided_by_hy_user_id: string;
  decided_at: string;
}

export interface CreatorPublicProfile {
  hy_user_id: string;
  hy_name: string;
  creator_handle: string;
  creator_bio: string | null;
  creator_socials: Record<string, string> | null;
  templates: Array<{
    id: string;
    name: string;
    slug: string | null;
    public_title: string | null;
    public_description: string | null;
    cover_media_url: string | null;
    cover_media_type: 'image' | 'video' | null;
    tags: string[] | null;
    updated_at: string;
  }>;
}

/**
 * Stored payload shape inside `agent_template_versions.payload`.
 * Mirrors the writeable subset of `agent_templates`.
 */
export type AgentVersionPayload = Pick<
  AgentTemplate,
  | 'name'
  | 'slug'
  | 'public_title'
  | 'public_description'
  | 'cover_media_url'
  | 'cover_media_type'
  | 'system_instructions'
  | 'guardrails'
  | 'onboarding_form_enabled'
  | 'onboarding_form_schema'
  | 'pricing'
  | 'knowledge_base_enabled'
  | 'knowledge_base_text'
  | 'file_uploads_enabled'
  | 'tool_serper'
  | 'tool_tavily'
  | 'voice_enabled'
  | 'voice_config'
  | 'openai_model'
  | 'tags'
> & {
  /** Creator-supplied changelog note shown in version history + review queue. */
  changelog?: string | null;
};

export interface PreflightIssue {
  code: string;
  severity: 'hard' | 'soft';
  message: string;
}

export interface PreflightReport {
  ok: boolean;
  hard_failures: PreflightIssue[];
  soft_warnings: PreflightIssue[];
  checked_at: string;
  checks_run: string[];
  moderation: {
    ran: boolean;
    flagged: boolean;
    categories?: Record<string, boolean>;
  };
}

export const VERSION_STATUS_LABEL: Record<AgentTemplateVersionStatus, string> = {
  draft: 'Draft',
  pending: 'In review',
  live: 'Live',
  superseded: 'Superseded',
  rejected: 'Rejected',
};

export const VERSION_STATUS_TONE: Record<AgentTemplateVersionStatus, string> = {
  draft: 'text-th-secondary bg-elevated border-subtle',
  pending: 'text-status-warning bg-status-warning/10 border-status-warning/30',
  live: 'text-status-success bg-status-success/10 border-status-success/30',
  superseded: 'text-th-tertiary bg-elevated border-subtle opacity-80',
  rejected: 'text-status-error bg-status-error/10 border-status-error/30',
};
